import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, AlertCircle, LogOut, Download, UserCog, Upload, Mail, Eye, Link as LinkIcon, Unlink, ExternalLink, HeartPulse, Bell, Ruler } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import BrandingPanel from "@/components/dashboard/BrandingPanel";
import BrowserAccessPanel from "@/components/dashboard/browser/BrowserAccessPanel";
import AiBudgetPanel from "@/components/dashboard/settings/AiBudgetPanel";
import BotPanel from "@/components/dashboard/settings/BotPanel";
import SafetySettingsTab from "@/components/dashboard/SafetySettingsTab";
import CloudApiSettings from "@/components/dashboard/settings/CloudApiSettings";
import CloudBackupSettings from "@/components/dashboard/settings/CloudBackupSettings";
import BotEnginePanel from "@/components/dashboard/settings/BotEnginePanel";
import PermissionsPanel from "@/components/dashboard/PermissionsPanel";
import ImpersonatePanel from "@/components/dashboard/ImpersonatePanel";
import SecurityLogPanel from "@/components/dashboard/SecurityLogPanel";
import DeployKeyPanel from "@/components/dashboard/settings/DeployKeyPanel";
import OnboardingSettings from "@/components/dashboard/settings/OnboardingSettings";
import CompanyPanel from "@/components/dashboard/CompanyPanel";
import SherlockTab from "@/components/dashboard/SherlockTab";
import VideoLogTab from "@/components/dashboard/VideoLogTab";
import ServerSnapshotsPanel from "@/components/dashboard/ServerSnapshotsPanel";
import WorkgroupsPanel from "@/components/dashboard/WorkgroupsPanel";
import BankingSettingsPanel from "@/components/dashboard/BankingSettingsPanel";
import ThemePanel from "@/components/dashboard/ThemePanel";
import AgentsPanel from "@/components/dashboard/settings/AgentsPanel";
import { AGENTS_UI_PREVIEW } from "@/lib/featureFlags";
import { useWorkgroup, WG_COLORS } from "@/components/dashboard/WorkgroupBar";

const SettingsDialog = ({ open, onOpenChange }) => {
  const { user, logout } = useAuth();
  // H-6: superadmin = administration; owner = business settings only.
  const isSuper = user?.role === "superadmin";
  const isBizOwner = isSuper || user?.role === "owner";
  const workgroup = useWorkgroup();
  const [status, setStatus] = useState(null);
  const [qboStatus, setQboStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [qboClientId, setQboClientId] = useState("");
  const [qboClientSecret, setQboClientSecret] = useState("");
  const [qboProdClientId, setQboProdClientId] = useState("");
  const [qboProdClientSecret, setQboProdClientSecret] = useState("");
  const [qboEnv, setQboEnv] = useState("sandbox");
  const [saving, setSaving] = useState(false);
  const [savingQbo, setSavingQbo] = useState(false);
  const [disconnectingQbo, setDisconnectingQbo] = useState(false);

  // Health benefits editor — per-employee yearly total + claimed so far
  const [benEmps, setBenEmps] = useState([]);
  const [benEmp, setBenEmp] = useState("");
  const [benInfo, setBenInfo] = useState(null);
  const [benAllow, setBenAllow] = useState("");
  const [benClaimed, setBenClaimed] = useState("");
  const [benBusy, setBenBusy] = useState(false);

  // ---- per-user notification preferences (Settings → Notifications) ----
  const [notifPrefs, setNotifPrefs] = useState(null); // {kinds:[], prefs:{}}
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/me/notification-prefs");
        setNotifPrefs(data);
      } catch { setNotifPrefs(null); }
    })();
  }, [open]);
  const toggleNotif = async (kind, val) => {
    const prev = notifPrefs;
    const next = { ...prev, prefs: { ...prev.prefs, [kind]: val } };
    setNotifPrefs(next);
    try {
      await api.put("/me/notification-prefs", { prefs: next.prefs });
    } catch {
      setNotifPrefs(prev);
      toast.error("Couldn't save notification settings");
    }
  };

  useEffect(() => {
    if (!open || !isBizOwner) return;
    api.get("/employees")
      .then(({ data }) => setBenEmps((data || []).filter((e) => (e.status || "Active") === "Active")))
      .catch(() => {});
  }, [open, isBizOwner]);

  const loadBenefits = async (id) => {
    setBenEmp(id);
    setBenInfo(null);
    if (!id) return;
    try {
      const { data } = await api.get(`/employees/${id}/benefits`);
      setBenInfo(data);
      setBenAllow(data.allowance ? String(data.allowance) : "");
      setBenClaimed(String(data.used ?? 0));
    } catch { toast.error("Couldn't load that employee's benefits"); }
  };

  const saveBenefits = async () => {
    setBenBusy(true);
    try {
      await api.put(`/employees/${benEmp}/benefits`, {
        allowance: parseFloat(benAllow) || 0,
        claimed: parseFloat(benClaimed) || 0,
      });
      toast.success("Benefits updated — the employee's claim card reflects it instantly");
      loadBenefits(benEmp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save benefits");
    } finally { setBenBusy(false); }
  };
  // App-wide feature flags (admins only) — currently just health benefits.
  const [features, setFeatures] = useState(null);

  useEffect(() => {
    if (open && isBizOwner) {
      api.get("/settings/features").then((r) => setFeatures(r.data)).catch(() => setFeatures(null));
    }
  }, [open, isBizOwner]);

  const toggleBenefits = async (val) => {
    setFeatures((f) => ({ ...(f || {}), health_benefits_enabled: val }));
    try {
      const { data } = await api.put("/settings/features", { health_benefits_enabled: val });
      setFeatures(data);
      toast.success(val ? "Health benefits turned on" : "Health benefits turned off");
    } catch {
      setFeatures((f) => ({ ...(f || {}), health_benefits_enabled: !val }));
      toast.error("Could not update feature settings");
    }
  };

  const setEstVersion = async (v) => {
    const prev = features?.estimate_version || "2";
    setFeatures((f) => ({ ...(f || {}), estimate_version: v }));
    try {
      const { data } = await api.put("/settings/features", { estimate_version: v });
      setFeatures(data);
      toast.success(`Estimating tab set to version ${v} — reopen the tab to see it`);
    } catch {
      setFeatures((f) => ({ ...(f || {}), estimate_version: prev }));
      toast.error("Could not update feature settings");
    }
  };

  const loadStatus = async () => {
    if (!isSuper) return; // H-6: integration settings are superadmin only
    try {
      const r = await api.get("/settings/integrations");
      setStatus(r.data.gmail);
      setQboStatus(r.data.qbo);
      if (r.data.qbo?.environment) setQboEnv(r.data.qbo.environment);
    } catch {
      setStatus(null);
      setQboStatus(null);
    }
  };

  useEffect(() => {
    if (open) {
      loadStatus();
      setClientId("");
      setClientSecret("");
      setQboClientId("");
      setQboClientSecret("");
      setQboProdClientId("");
      setQboProdClientSecret("");
    }
  }, [open]);

  // Refresh QBO status when the tab-visibility changes (user returned from Intuit OAuth in a new tab).
  useEffect(() => {
    if (!open) return;
    const onVisible = () => { if (!document.hidden) loadStatus(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [open]);

  const save = async () => {
    if (!clientId.trim() && !clientSecret.trim()) {
      toast.error("Enter your Google Client ID and Secret");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/settings/integrations", {
        google_client_id: clientId.trim() || undefined,
        google_client_secret: clientSecret.trim() || undefined,
      });
      setStatus(r.data.gmail);
      setClientSecret("");
      toast.success("API credentials saved");
    } catch {
      toast.error("Could not save credentials");
    } finally {
      setSaving(false);
    }
  };

  const saveQbo = async () => {
    const anyProvided = qboClientId.trim() || qboClientSecret.trim() || qboProdClientId.trim() || qboProdClientSecret.trim();
    if (!anyProvided && (qboEnv === qboStatus?.environment)) {
      toast.error("Nothing to save — change env or paste new keys");
      return;
    }
    setSavingQbo(true);
    try {
      const r = await api.put("/settings/integrations", {
        qbo_client_id: qboClientId.trim() || undefined,
        qbo_client_secret: qboClientSecret.trim() || undefined,
        qbo_prod_client_id: qboProdClientId.trim() || undefined,
        qbo_prod_client_secret: qboProdClientSecret.trim() || undefined,
        qbo_environment: qboEnv,
      });
      setQboStatus(r.data.qbo);
      setQboClientSecret("");
      setQboProdClientSecret("");
      toast.success("QuickBooks credentials saved");
    } catch {
      toast.error("Could not save QuickBooks credentials");
    } finally {
      setSavingQbo(false);
    }
  };

  const connectQbo = () => {
    if (!qboStatus?.configured) {
      toast.error("Save QuickBooks Client ID + Secret first");
      return;
    }
    // Full-page navigate so the Intuit OAuth flow can redirect back to us cleanly.
    window.location.href = `${window.location.origin}/api/oauth/qbo/login`;
  };

  const disconnectQbo = async () => {
    if (!window.confirm("Disconnect QuickBooks? You'll need to reconnect to see invoices/estimates.")) return;
    setDisconnectingQbo(true);
    try {
      await api.post("/qbo/disconnect");
      toast.success("QuickBooks disconnected");
      await loadStatus();
    } catch {
      toast.error("Could not disconnect");
    } finally {
      setDisconnectingQbo(false);
    }
  };

  const [backupPassword, setBackupPassword] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState("replace");
  const [restoring, setRestoring] = useState(false);
  const [exitEmailOn, setExitEmailOn] = useState(false);
  const [exitEmailBusy, setExitEmailBusy] = useState(false);
  const [autoRemindersOn, setAutoRemindersOn] = useState(false);
  const [autoRemindersBusy, setAutoRemindersBusy] = useState(false);
  const [exitPreview, setExitPreview] = useState(null); // {to, subject, html_body, attachment_name, attachment_size, pdf_base64}
  const [previewBusy, setPreviewBusy] = useState(false);
  const loadExitPreview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.get("/settings/exit-email/preview");
      setExitPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build preview");
    } finally {
      setPreviewBusy(false);
    }
  };
  const downloadPreviewPdf = () => {
    if (!exitPreview?.pdf_base64) return;
    const bytes = Uint8Array.from(atob(exitPreview.pdf_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = exitPreview.attachment_name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };
  useEffect(() => {
    if (!open || !isBizOwner) return;
    api.get("/settings/exit-email").then((r) => setExitEmailOn(!!r.data?.enabled)).catch(() => {});
    api.get("/settings/auto-reminders").then((r) => setAutoRemindersOn(!!r.data?.enabled)).catch(() => {});
  }, [open, isBizOwner]);
  const toggleExitEmail = async () => {
    const next = !exitEmailOn;
    setExitEmailBusy(true);
    try {
      await api.put("/settings/exit-email", { enabled: next });
      setExitEmailOn(next);
      toast.success(next ? "Exit email enabled" : "Exit email disabled");
    } catch {
      toast.error("Could not update setting");
    } finally {
      setExitEmailBusy(false);
    }
  };
  const toggleAutoReminders = async () => {
    const next = !autoRemindersOn;
    setAutoRemindersBusy(true);
    try {
      await api.put("/settings/auto-reminders", { enabled: next });
      setAutoRemindersOn(next);
      toast.success(next ? "Weekly overdue reminders enabled" : "Weekly overdue reminders disabled");
    } catch {
      toast.error("Could not update setting");
    } finally {
      setAutoRemindersBusy(false);
    }
  };
  const downloadBackup = async () => {
    if (!backupPassword.trim()) {
      toast.error("Enter the superadmin password");
      return;
    }
    setDownloading(true);
    try {
      // H-1: the password goes in the request BODY (never the URL, which
      // proxies and the backend would log).
      const r = await api.post("/admin/export-zip", { password: backupPassword.trim() }, {
        responseType: "blob",
      });
      const included = r.headers?.["x-uploads-included"] || "0";
      const missing = r.headers?.["x-uploads-missing"] || "0";
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `elite-backup-${ts}.zip`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`Backup ready · ${included} file(s) included${Number(missing) > 0 ? `, ${missing} missing on disk` : ""}`);
    } catch (e) {
      // Errors come back as a Blob (responseType: "blob"): unwrap {"detail"}.
      let detail = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) {
        const text = await e.response.data.text().catch(() => "");
        try { detail = JSON.parse(text)?.detail || text; } catch { detail = text; }
      }
      toast.error(detail || "Backup download failed");
    } finally {
      setDownloading(false);
    }
  };

  const restoreBackup = async () => {
    if (!backupPassword.trim()) {
      toast.error("Enter the superadmin password");
      return;
    }
    if (!restoreFile) {
      toast.error("Choose a backup .zip file first");
      return;
    }
    if (restoreMode === "replace" && !window.confirm(
      "REPLACE mode will WIPE customers, projects, billing, employees, files metadata and every /app/uploads blob before restoring. Continue?"
    )) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      // H-1: password as a multipart form field, never a URL parameter.
      fd.append("password", backupPassword.trim());
      const r = await api.post("/admin/import-zip", fd, {
        params: { mode: restoreMode },
        headers: { "Content-Type": "multipart/form-data" },
      });
      const counts = r.data?.imported || {};
      const total = Object.values(counts).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`Restore done · ${total} rows across ${Object.keys(counts).length} collections · ${r.data?.uploads_written || 0} file(s) written`);
      setRestoreFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* While a fullscreen portal (video-log replay / live view) sits above
          this dialog, taps inside it are "outside" this dialog to Radix —
          don't let them close settings out from under the viewer. */}
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] overflow-y-auto overflow-x-hidden bg-[#0d0d0d] text-white sm:max-w-2xl sm:w-full [&_*]:min-w-0 [&_select]:max-w-full [&_input]:max-w-full [&_textarea]:max-w-full break-words" data-testid="settings-dialog"
        onPointerDownOutside={(e) => { if (window.__videoLogOpen || window.__liveViewOpen) e.preventDefault(); }}
        onInteractOutside={(e) => { if (window.__videoLogOpen || window.__liveViewOpen) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (window.__videoLogOpen || window.__liveViewOpen) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Settings
            {user?.role === "superadmin" && workgroup && (
              <span
                className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${WG_COLORS[workgroup.slug]?.chip || ""}`}
                data-testid="settings-workgroup-chip"
              >
                {workgroup.name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {user?.role === "superadmin" && workgroup
              ? `Every setting here applies to the ${workgroup.name} workgroup only.`
              : "Manage your portal integrations and API credentials."}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={isSuper ? "permissions" : "general"} className="w-full">
          <TabsList className="flex h-auto w-full flex-wrap gap-1 bg-white/5 p-1">
            {isSuper && (
              <TabsTrigger value="permissions" data-testid="settings-tab-permissions" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Users
              </TabsTrigger>
            )}
            <TabsTrigger value="general" data-testid="settings-tab-general" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
              General
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
              Notifications
            </TabsTrigger>
            {isBizOwner && (
              <TabsTrigger value="benefits" data-testid="settings-tab-benefits" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Benefits
              </TabsTrigger>
            )}
            {isSuper && (
            <TabsTrigger value="api" data-testid="settings-tab-api" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
              API
            </TabsTrigger>
            )}
            {user?.role === "superadmin" && (
              <TabsTrigger value="safety" data-testid="settings-tab-safety" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Safety
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="bot" data-testid="settings-tab-bot" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Bot
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="ai" data-testid="settings-tab-ai" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                AI Use
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="theme" data-testid="settings-tab-theme" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Theme
              </TabsTrigger>
            )}
            {user?.role === "superadmin" && (
              <TabsTrigger value="branding" data-testid="settings-tab-branding" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Branding
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="prompts" data-testid="settings-tab-prompts" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Prompts
              </TabsTrigger>
            )}
            {isBizOwner && (
              <TabsTrigger value="company" data-testid="settings-tab-company" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Company
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="backup" data-testid="settings-tab-backup" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Backup
              </TabsTrigger>
            )}
            {isBizOwner && (
              <TabsTrigger value="onboarding" data-testid="settings-tab-onboarding" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Onboarding
              </TabsTrigger>
            )}
            {user?.role === "superadmin" && (
              <TabsTrigger value="workgroups" data-testid="settings-tab-workgroups" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Workgroups
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="security" data-testid="settings-tab-security" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Security
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="impersonate" data-testid="settings-tab-impersonate" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Impersonate
              </TabsTrigger>
            )}
            {user?.role === "superadmin" && (
              <TabsTrigger value="sherlock" data-testid="settings-tab-sherlock" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Sherlock
              </TabsTrigger>
            )}
            {user?.role === "superadmin" && (
              <TabsTrigger value="videolog" data-testid="settings-tab-videolog" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Video Log
              </TabsTrigger>
            )}
            {AGENTS_UI_PREVIEW && user?.role === "superadmin" && (
              <TabsTrigger value="agents" data-testid="settings-tab-agents" className="px-2 py-1.5 text-xs data-[state=active]:bg-[#1a3fd1] data-[state=active]:text-white sm:px-3 sm:text-sm">
                Agents
              </TabsTrigger>
            )}
          </TabsList>

          {isSuper && (
            <TabsContent value="theme" className="pt-4" data-testid="settings-theme-panel">
              <ThemePanel />
            </TabsContent>
          )}
          <TabsContent value="general" className="space-y-4 pt-4" data-testid="settings-general-panel">
            <p className="text-sm text-white/50">Signed in as <span className="text-white">{user?.email}</span> ({user?.role}).</p>
            {isBizOwner && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="flex items-center gap-2">
                  <HeartPulse className="h-4 w-4 shrink-0 text-emerald-400" />
                  <div>
                    <p className="text-sm font-semibold text-white">Health benefits</p>
                    <p className="text-xs text-white/45">When off, everyone sees &quot;health benefits are not currently being offered&quot; in Requests</p>
                  </div>
                </div>
                <Switch
                  checked={!!features?.health_benefits_enabled}
                  onCheckedChange={toggleBenefits}
                  disabled={!features}
                  data-testid="toggle-health-benefits"
                />
              </div>
            )}
            {isSuper && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 shrink-0 text-[#8fa4f0]" />
                  <div>
                    <p className="text-sm font-semibold text-white">Estimating tab</p>
                    <p className="text-xs text-white/45">Which estimating experience everyone gets — Estimate (classic) or AI</p>
                  </div>
                </div>
                <div className="flex overflow-hidden rounded-md border border-white/15" data-testid="estimate-version-toggle">
                  {[["1", "AI"], ["2", "New"]].map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setEstVersion(v)}
                      disabled={!features}
                      data-testid={`estimate-version-${v}`}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                        (features?.estimate_version || "2") === v
                          ? "bg-[#1a3fd1] text-white"
                          : "bg-white/5 text-white/55 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={logout} data-testid="settings-signout-btn" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-3 pt-4" data-testid="settings-notifications-panel">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-[#f97316]" />
              <h3 className="text-sm font-semibold">What reaches you</h3>
            </div>
            <p className="text-xs text-white/50">
              Turn off anything you don't want — unchecked items stop completely: no bell entry and no push to your phone. Changes save instantly.
            </p>
            {!notifPrefs && <p className="text-xs text-white/40">Loading…</p>}
            {notifPrefs && notifPrefs.kinds.length === 0 && (
              <p className="text-xs text-white/40">No notification types available for your account.</p>
            )}
            {notifPrefs && notifPrefs.kinds.map((k) => (
              <div key={k.kind} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`notif-pref-row-${k.kind}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{k.label}</p>
                  <p className="text-xs text-white/45">{k.desc}</p>
                </div>
                <Switch
                  checked={notifPrefs.prefs[k.kind] !== false}
                  onCheckedChange={(v) => toggleNotif(k.kind, v)}
                  data-testid={`notif-pref-${k.kind}`}
                />
              </div>
            ))}

            {isBizOwner && (
              <div className="mt-6 space-y-4 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="auto-reminders-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-4 w-4 text-amber-300" />
                    <div>
                      <p className="text-sm font-semibold text-white">Automatic weekly overdue reminders</p>
                      <p className="text-xs text-white/40">
                        When on, any customer with a synced QuickBooks invoice past its due date gets a polite
                        reminder email — listing their unpaid invoices and total — at most once a week, sent from
                        your connected mailbox. Off by default; turn it on once invoice statuses are up to date.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoRemindersOn}
                    onClick={toggleAutoReminders}
                    disabled={autoRemindersBusy}
                    data-testid="auto-reminders-toggle"
                    className={`flex h-6 w-11 shrink-0 items-center rounded-full border p-0.5 transition-colors ${
                      autoRemindersOn ? "justify-end border-emerald-400 bg-emerald-500/40" : "justify-start border-white/20 bg-white/10"
                    } disabled:opacity-60`}
                  >
                    <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
                  </button>
                </div>
              </div>
            )}
            {isBizOwner && (
              <div className="mt-6 space-y-4 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="exit-email-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-4 w-4 text-emerald-300" />
                    <div>
                      <p className="text-sm font-semibold text-white">Email exit snapshot to departing employees</p>
                      <p className="text-xs text-white/40">
                        When on, the exit-snapshot PDF (last paystub + T4-style YTD) is auto-emailed from
                        your IMAP mailbox to the employee&apos;s address the moment they&apos;re flipped to Inactive.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={exitEmailOn}
                    onClick={toggleExitEmail}
                    disabled={exitEmailBusy}
                    data-testid="exit-email-toggle"
                    className={`flex h-6 w-11 shrink-0 items-center rounded-full border p-0.5 transition-colors ${
                      exitEmailOn ? "justify-end border-emerald-400 bg-emerald-500/40" : "justify-start border-white/20 bg-white/10"
                    } disabled:opacity-60`}
                  >
                    <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={loadExitPreview}
                    disabled={previewBusy}
                    data-testid="exit-email-preview-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-60"
                  >
                    <Eye className="h-3.5 w-3.5" /> {previewBusy ? "Building…" : "Preview email"}
                  </button>
                </div>
              </div>
            )}
          </TabsContent>

          {isBizOwner && (
          <TabsContent value="benefits" className="space-y-4 pt-4" data-testid="settings-benefits-panel">
            <div className="space-y-3 rounded-lg border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-2">
                <HeartPulse className="h-4 w-4 text-emerald-400" />
                <h3 className="text-sm font-semibold">Health benefits — per employee</h3>
              </div>
              <p className="text-xs text-white/50">
                Set each employee's yearly total available and how much they've claimed so far. Their claim card shows the new numbers instantly.
              </p>
              <select
                value={benEmp}
                onChange={(e) => loadBenefits(e.target.value)}
                data-testid="settings-benefits-employee"
                className="h-10 w-full rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
              >
                <option value="">— Pick an employee —</option>
                {benEmps.map((e) => (
                  <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
                ))}
              </select>
              {benInfo && (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-white/50">Total available in {benInfo.year}</Label>
                      <Input type="number" min="0" step="0.01" value={benAllow} onChange={(e) => setBenAllow(e.target.value)} placeholder="500.00" data-testid="settings-benefits-allowance" className="border-white/15 bg-white/5 text-white" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-white/50">Claimed so far</Label>
                      <Input type="number" min="0" step="0.01" value={benClaimed} onChange={(e) => setBenClaimed(e.target.value)} placeholder="0.00" data-testid="settings-benefits-claimed" className="border-white/15 bg-white/5 text-white" />
                    </div>
                  </div>
                  <p className="font-mono text-xs text-white/60" data-testid="settings-benefits-remaining">
                    Remaining this year: ${Math.max(0, (parseFloat(benAllow) || 0) - (parseFloat(benClaimed) || 0)).toFixed(2)}
                  </p>
                  <Button onClick={saveBenefits} disabled={benBusy} data-testid="settings-benefits-save" className="bg-[#1a3fd1] text-white hover:bg-[#1633a3]">
                    {benBusy ? "Saving…" : "Save benefits"}
                  </Button>
                </>
              )}
            </div>
          </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="bot" className="space-y-5 pt-4" data-testid="settings-bot-tab">
              <BotPanel />
            </TabsContent>
          )}
          {user?.role === "superadmin" && (
            <TabsContent value="safety" className="pt-4">
              <SafetySettingsTab />
            </TabsContent>
          )}
          {isSuper && (
            <TabsContent value="ai" className="space-y-5 pt-4" data-testid="settings-ai-tab">
              <BotEnginePanel />
            </TabsContent>
          )}

          {isSuper && (
          <TabsContent value="api" className="space-y-5 pt-4" data-testid="settings-api-panel">
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
              <KeyRound className="h-4 w-4 text-[#4f6fe8]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Gmail (Google API)</p>
                <p className="text-xs text-white/40">Used to connect email accounts in the Email tab.</p>
              </div>
              {status?.configured ? (
                <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400" data-testid="gmail-status">
                  <CheckCircle2 className="h-4 w-4" /> Configured
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs font-medium text-amber-400" data-testid="gmail-status">
                  <AlertCircle className="h-4 w-4" /> Not set
                </span>
              )}
            </div>

            <div>
              <Label htmlFor="gclientid" className="text-xs text-white/50">
                Google Client ID {status?.client_id_masked ? `(current: ${status.client_id_masked})` : ""}
              </Label>
              <Input
                id="gclientid"
                data-testid="google-client-id-input"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="xxxxxx.apps.googleusercontent.com"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="gsecret" className="text-xs text-white/50">
                Google Client Secret {status?.has_secret ? "(saved — leave blank to keep)" : ""}
              </Label>
              <Input
                id="gsecret"
                type="password"
                data-testid="google-client-secret-input"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="••••••••••••"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/40" data-testid="google-setup-help">
              <p className="mb-1 font-semibold text-white/70">How to get these ({workgroup?.name || "this workgroup"} needs its own):</p>
              <ol className="list-decimal space-y-0.5 pl-4">
                <li>Open <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-[#8fa4f0] underline">Google Cloud → APIs &amp; Services → Credentials</a> and create (or pick) a project for {workgroup?.name || "this company"}.</li>
                <li>Enable the <a href="https://console.cloud.google.com/apis/library/gmail.googleapis.com" target="_blank" rel="noreferrer" className="text-[#8fa4f0] underline">Gmail API</a> for that project.</li>
                <li>Create credentials → <strong>OAuth client ID</strong> → type <strong>Web application</strong>, then paste the two values below into it.</li>
                <li>Copy the generated Client ID and Client Secret into the fields above and save.</li>
              </ol>
              <br />• Authorized redirect URI:{" "}
              <span className="break-all font-mono text-[#8fa4f0]" data-testid="gmail-redirect-uri">
                {`${workgroup?.base_url || window.location.origin}/api/oauth/gmail/callback`}
              </span>
              <br />• Authorized JavaScript origin:{" "}
              <span className="break-all font-mono text-[#8fa4f0]">{workgroup?.base_url || window.location.origin}</span>
              {status?.last_connect && (
                <>
                  <br />• Last connect attempt:{" "}
                  {status.last_connect.ok ? (
                    <span className="font-semibold text-emerald-400" data-testid="gmail-last-connect">
                      succeeded ({status.last_connect.detail || "saved"})
                    </span>
                  ) : (
                    <span className="font-semibold text-red-400" data-testid="gmail-last-connect">
                      failed at the {status.last_connect.why} step
                      {status.last_connect.detail ? ` — ${status.last_connect.detail}` : ""}
                    </span>
                  )}{" "}
                  <span className="text-white/30">
                    ({new Date(status.last_connect.at).toLocaleString("en-CA")})
                  </span>
                </>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-api-keys-btn" className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
                {saving ? "Saving…" : "Save credentials"}
              </Button>
            </div>

            <CloudApiSettings workgroupName={workgroup?.name} />

            {/* -------------------- QuickBooks Online -------------------- */}
            <div className="mt-8 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
              <KeyRound className="h-4 w-4 text-emerald-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">QuickBooks Online</p>
                <p className="text-xs text-white/40">Pulls Invoices &amp; Estimates into each Customer page.</p>
              </div>
              {qboStatus?.connected && qboStatus?.needs_reconnect ? (
                <span className="flex items-center gap-1.5 text-xs font-medium text-red-400" data-testid="qbo-status">
                  <AlertCircle className="h-4 w-4" /> Reconnect needed
                </span>
              ) : qboStatus?.connected ? (
                <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400" data-testid="qbo-status">
                  <CheckCircle2 className="h-4 w-4" /> Connected
                </span>
              ) : qboStatus?.configured ? (
                <span className="flex items-center gap-1.5 text-xs font-medium text-amber-400" data-testid="qbo-status">
                  <AlertCircle className="h-4 w-4" /> Not connected
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs font-medium text-white/50" data-testid="qbo-status">
                  <AlertCircle className="h-4 w-4" /> Not set
                </span>
              )}
            </div>

            {qboStatus?.connected && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3 text-xs text-emerald-100">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-emerald-200">{qboStatus.company_name || "QuickBooks company"}</p>
                    <p className="font-mono text-[10px] text-emerald-300/70">
                      Realm {qboStatus.realm_id} · {qboStatus.environment}
                    </p>
                  </div>
                  <Button
                    onClick={disconnectQbo}
                    disabled={disconnectingQbo}
                    data-testid="qbo-disconnect-btn"
                    variant="outline"
                    className="border-red-400/40 bg-transparent text-red-200 hover:bg-red-500/10"
                  >
                    <Unlink className="mr-1.5 h-3.5 w-3.5" />
                    {disconnectingQbo ? "…" : "Disconnect"}
                  </Button>
                </div>
                {(qboStatus.last_refresh_tid || qboStatus.last_error) && (
                  <div className="mt-3 space-y-1 border-t border-emerald-500/20 pt-2 font-mono text-[10px] text-emerald-300/70">
                    {qboStatus.last_refresh_tid && (
                      <p data-testid="qbo-last-tid">
                        Last <span className="text-emerald-200">intuit_tid</span>:{" "}
                        <span className="break-all text-white/80">{qboStatus.last_refresh_tid}</span>
                      </p>
                    )}
                    {qboStatus.last_error && (
                      <p className="text-red-300" data-testid="qbo-last-error">
                        Last error {qboStatus.last_error.status}{qboStatus.last_error.code ? ` · ${qboStatus.last_error.code}` : ""} · tid {qboStatus.last_error.intuit_tid || "n/a"} @ {qboStatus.last_error.at?.slice(0, 19)}
                      </p>
                    )}
                    {qboStatus.needs_reconnect && (
                      <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 font-sans text-[11px] text-red-100" data-testid="qbo-reconnect-hint">
                        <span>
                          {qboStatus.last_error?.code === "invalid_grant"
                            ? "Intuit rejected the saved refresh token — it expired or was revoked (this happens if QuickBooks was disconnected from Intuit's side, or the app wasn't opened for 100 days). Reconnect to get a fresh one."
                            : qboStatus.last_error?.code === "invalid_client"
                              ? `The Client ID / Secret saved for ${qboStatus.environment} doesn't match Intuit's — check them above, then reconnect.`
                              : "The last token refresh failed, so syncs will keep failing until you reconnect."}
                        </span>
                        <Button onClick={connectQbo} size="sm" className="shrink-0 bg-red-500 text-white hover:bg-red-600" data-testid="qbo-reconnect-btn">
                          Reconnect
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs text-white/50">Environment</Label>
              <div className="mt-1.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => setQboEnv("sandbox")}
                  data-testid="qbo-env-sandbox"
                  className={`flex-1 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                    qboEnv === "sandbox"
                      ? "border-emerald-400 bg-emerald-400/10 text-white"
                      : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30"
                  }`}
                >
                  Sandbox (dev)
                </button>
                <button
                  type="button"
                  onClick={() => setQboEnv("production")}
                  data-testid="qbo-env-production"
                  className={`flex-1 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                    qboEnv === "production"
                      ? "border-[#1a3fd1] bg-[#1a3fd1]/20 text-white"
                      : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30"
                  }`}
                >
                  Production
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="qboClientId" className="text-xs text-emerald-300/80">
                Sandbox — Client ID {qboStatus?.sandbox?.client_id_masked ? `(current: ${qboStatus.sandbox.client_id_masked})` : ""}
              </Label>
              <Input
                id="qboClientId"
                data-testid="qbo-client-id-input"
                value={qboClientId}
                onChange={(e) => setQboClientId(e.target.value)}
                placeholder="ABxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="qboClientSecret" className="text-xs text-emerald-300/80">
                Sandbox — Client Secret {qboStatus?.sandbox?.has_secret ? "(saved — leave blank to keep)" : ""}
              </Label>
              <Input
                id="qboClientSecret"
                type="password"
                data-testid="qbo-client-secret-input"
                value={qboClientSecret}
                onChange={(e) => setQboClientSecret(e.target.value)}
                placeholder="••••••••••••"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            <div>
              <Label htmlFor="qboProdClientId" className="text-xs text-[#8fa4f0]">
                Production — Client ID {qboStatus?.production?.client_id_masked ? `(current: ${qboStatus.production.client_id_masked})` : ""}
              </Label>
              <Input
                id="qboProdClientId"
                data-testid="qbo-prod-client-id-input"
                value={qboProdClientId}
                onChange={(e) => setQboProdClientId(e.target.value)}
                placeholder="ABxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="qboProdClientSecret" className="text-xs text-[#8fa4f0]">
                Production — Client Secret {qboStatus?.production?.has_secret ? "(saved — leave blank to keep)" : ""}
              </Label>
              <Input
                id="qboProdClientSecret"
                type="password"
                data-testid="qbo-prod-client-secret-input"
                value={qboProdClientSecret}
                onChange={(e) => setQboProdClientSecret(e.target.value)}
                placeholder="••••••••••••"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/40" data-testid="qbo-setup-help">
              <p className="mb-1 font-semibold text-white/70">How to get these ({workgroup?.name || "this workgroup"} needs its own Intuit app):</p>
              <ol className="list-decimal space-y-0.5 pl-4">
                <li>Sign in at <a href="https://developer.intuit.com/app/developer/myapps" target="_blank" rel="noreferrer" className="text-[#8fa4f0] underline">developer.intuit.com → My Apps</a> with the Intuit account that owns {workgroup?.name || "this company"}&apos;s QuickBooks.</li>
                <li><strong>Create an app</strong> → QuickBooks Online and Payments → scope <em>Accounting</em>.</li>
                <li>Under <strong>Keys &amp; credentials</strong> copy the Development (sandbox) and Production Client ID / Secret into the fields above.</li>
                <li>On the same page add the Redirect URI below (exact match), then save here and click Connect QuickBooks.</li>
              </ol>
              <br />Redirect URI:{" "}
              <span className="break-all font-mono text-emerald-300" data-testid="qbo-redirect-uri">{`${workgroup?.base_url || window.location.origin}/api/oauth/qbo/callback`}</span>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                onClick={saveQbo}
                disabled={savingQbo}
                data-testid="save-qbo-keys-btn"
                variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/10"
              >
                {savingQbo ? "Saving…" : "Save credentials"}
              </Button>
              <Button
                onClick={connectQbo}
                disabled={!qboStatus?.configured}
                data-testid="qbo-connect-btn"
                className="bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40"
              >
                <LinkIcon className="mr-1.5 h-3.5 w-3.5" />
                {qboStatus?.connected ? "Reconnect QuickBooks" : "Connect QuickBooks"}
                <ExternalLink className="ml-1.5 h-3 w-3" />
              </Button>
            </div>

            <BankingSettingsPanel workgroup={workgroup} />
          </TabsContent>
          )}

          {user?.role === "superadmin" && (
            <TabsContent value="branding" className="space-y-5 pt-4" data-testid="settings-branding-panel">
              <BrandingPanel />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="prompts" className="space-y-5 pt-4" data-testid="settings-prompts-panel">
              <PromptEditor kind="analyze" label="Analyze prompt" description="Runs when the Analyze button is clicked on an estimate. This is the system message sent to Claude." />
              <PromptEditor kind="estimate" label="Estimate prompt" description="Runs when the Estimate button is clicked. Should ask Claude for a full line-item cost estimate." />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="permissions" className="pt-4" data-testid="settings-permissions-panel">
              <PermissionsPanel />
              <BrowserAccessPanel />
              <AiBudgetPanel />
            </TabsContent>
          )}

          {isBizOwner && (
            <TabsContent value="company" className="space-y-5 pt-4" data-testid="settings-company-panel">
              <CompanyPanel />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="backup" className="space-y-5 pt-4" data-testid="settings-backup-panel">
              <ServerSnapshotsPanel />
              <CloudBackupSettings />

              <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <Download className="mt-0.5 h-4 w-4 text-[#4f6fe8]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">Full backup (.zip)</p>
                  <p className="text-xs text-white/40">
                    Bundles customers, projects, billing, employees, the file explorer index and every
                    physical upload under <span className="font-mono">/app/uploads</span>. Restore later
                    with <span className="font-mono">POST /api/admin/import-zip</span>.
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="backup-pw" className="text-xs text-white/50">
                  Superadmin password
                </Label>
                <Input
                  id="backup-pw"
                  type="password"
                  data-testid="backup-password-input"
                  value={backupPassword}
                  onChange={(e) => setBackupPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 border-white/15 bg-white/5 text-white"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={downloadBackup}
                  disabled={downloading}
                  data-testid="download-backup-btn"
                  className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloading ? "Preparing…" : "Download full backup"}
                </Button>
              </div>

              {/* Restore is destructive: superadmin only (backend enforces it too). */}
              {user?.role === "superadmin" && (
              <div className="mt-6 space-y-4 rounded-lg border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-start gap-3">
                  <Upload className="mt-0.5 h-4 w-4 text-amber-300" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Restore from a backup .zip</p>
                    <p className="text-xs text-white/40">
                      Rehydrates every collection AND writes every physical upload back to disk. Uses the
                      same superadmin password above.
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="restore-file" className="text-xs text-white/50">Backup archive</Label>
                  <Input
                    id="restore-file"
                    type="file"
                    accept=".zip,application/zip"
                    onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                    data-testid="restore-file-input"
                    className="mt-1 cursor-pointer border-white/15 bg-white/5 text-white file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-white"
                  />
                </div>

                <div>
                  <Label className="text-xs text-white/50">Restore mode</Label>
                  <div className="mt-1.5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRestoreMode("replace")}
                      data-testid="restore-mode-replace"
                      className={`flex-1 rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                        restoreMode === "replace"
                          ? "border-amber-400 bg-amber-400/10 text-white"
                          : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30"
                      }`}
                    >
                      <span className="block text-sm font-semibold">Replace</span>
                      <span className="block text-white/50">Wipes every collection + /app/uploads first (safest for a true restore).</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRestoreMode("merge")}
                      data-testid="restore-mode-merge"
                      className={`flex-1 rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                        restoreMode === "merge"
                          ? "border-emerald-400 bg-emerald-400/10 text-white"
                          : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30"
                      }`}
                    >
                      <span className="block text-sm font-semibold">Merge</span>
                      <span className="block text-white/50">Inserts rows on top of what&apos;s already here. Existing uploads stay.</span>
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={restoreBackup}
                    disabled={restoring || !restoreFile}
                    data-testid="restore-backup-btn"
                    className={
                      restoreMode === "replace"
                        ? "bg-amber-500 text-black hover:bg-amber-400"
                        : "bg-emerald-500 text-black hover:bg-emerald-400"
                    }
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {restoring ? "Restoring…" : `Restore (${restoreMode})`}
                  </Button>
                </div>
              </div>
              )}
            </TabsContent>
          )}

          {isBizOwner && (
            <TabsContent value="onboarding" className="space-y-4 pt-4" data-testid="settings-onboarding-panel">
              <OnboardingSettings />
            </TabsContent>
          )}

          {user?.role === "superadmin" && (
            <TabsContent value="workgroups" className="space-y-4 pt-4" data-testid="settings-workgroups-panel">
              <WorkgroupsPanel />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="security" className="space-y-4 pt-4" data-testid="settings-security-panel">
              {user?.role === "superadmin" && <DeployKeyPanel />}
              <SecurityLogPanel />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="impersonate" className="space-y-4 pt-4" data-testid="settings-impersonate-panel">
              <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <UserCog className="mt-0.5 h-4 w-4 text-[#4f6fe8]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">Impersonate a user</p>
                  <p className="text-xs text-white/40">
                    View the portal exactly as another user sees it. Your session stays yours &mdash; every
                    action is still audited under your real account.
                  </p>
                </div>
              </div>
              <ImpersonatePanel onSwitched={() => onOpenChange?.(false)} />
            </TabsContent>
          )}
          {user?.role === "superadmin" && (
            <TabsContent value="sherlock" className="space-y-4 pt-4" data-testid="settings-sherlock-panel">
              <SherlockTab />
            </TabsContent>
          )}
          {user?.role === "superadmin" && (
            <TabsContent value="videolog" className="space-y-4 pt-4" data-testid="settings-videolog-panel">
              <VideoLogTab />
            </TabsContent>
          )}
          {AGENTS_UI_PREVIEW && user?.role === "superadmin" && (
            <TabsContent value="agents" className="pt-4" data-testid="settings-agents-panel">
              <AgentsPanel />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
      {exitPreview && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3"
          onClick={() => setExitPreview(null)}
          data-testid="exit-email-preview-dialog"
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">Exit email preview</p>
                <h3 className="text-lg font-bold uppercase tracking-tight">Sample for {exitPreview.employee_name}</h3>
              </div>
              <button onClick={() => setExitPreview(null)} className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25">
                <span className="sr-only">Close</span>×
              </button>
            </div>
            <div className="max-h-[70vh] space-y-3 overflow-y-auto p-4">
              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">To</p>
                <p className="text-sm text-white/90">{exitPreview.to}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40">Subject</p>
                <p className="text-sm text-white/90">{exitPreview.subject}</p>
              </div>
              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Body</p>
                <div
                  className="prose prose-invert mt-1 max-w-none text-sm text-white/85 [&_p]:my-1.5"
                  dangerouslySetInnerHTML={{ __html: exitPreview.html_body }}
                />
              </div>
              <div className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.03] p-3">
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Attachment</p>
                  <p className="truncate text-sm text-white/90">{exitPreview.attachment_name}</p>
                  <p className="font-mono text-[10px] text-white/40">{(exitPreview.attachment_size / 1024).toFixed(1)} KB · application/pdf</p>
                </div>
                <button
                  type="button"
                  onClick={downloadPreviewPdf}
                  data-testid="exit-email-preview-download"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85"
                >
                  <Download className="h-3.5 w-3.5" /> Download PDF
                </button>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Preview only — nothing was sent.
              </p>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default SettingsDialog;

// -------------------- Prompt editor (admin) --------------------
const PromptEditor = ({ kind, label, description }) => {
  const [content, setContent] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/prompts/${kind}`);
      setContent(r.data?.content || "");
      setIsCustom(!!r.data?.is_custom);
    } finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, [kind]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/prompts/${kind}`, { content });
      toast.success(`${label} saved`);
      load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const resetDefault = async () => {
    if (!window.confirm(`Reset ${label.toLowerCase()} to default?`)) return;
    setBusy(true);
    try {
      await api.put(`/prompts/${kind}`, { content: "" });
      toast.success("Reset to default");
      load();
    } catch { toast.error("Reset failed"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest text-white/70">{label}</Label>
          <p className="text-[11px] text-white/40">{description}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${
          isCustom ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/40"
        }`}>
          {isCustom ? "Custom" : "Default"}
        </span>
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={8}
        data-testid={`prompt-editor-${kind}`}
        disabled={!loaded}
        className="mt-3 w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 font-mono text-[11px] leading-relaxed text-white outline-none focus:border-[#1a3fd1]"
      />
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button onClick={resetDefault} variant="outline" disabled={busy || !isCustom} data-testid={`prompt-reset-${kind}`} className="border-white/15 bg-transparent text-white/70 hover:bg-white/5">
          Reset to default
        </Button>
        <Button onClick={save} disabled={busy || !content.trim()} data-testid={`prompt-save-${kind}`} className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
};
